import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const listProcurementDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const [requests, pos, vendors, invoices, budgets, budgetLines, audit] = await Promise.all([
      supabase.from("procurement_requests").select("*, procurement_request_lines(*)").eq("school_id", data.schoolId).order("created_at", { ascending: false }).limit(100),
      supabase.from("purchase_orders").select("*, purchase_order_lines(*), vendors(vendor_name)").eq("school_id", data.schoolId).order("created_at", { ascending: false }).limit(50),
      supabase.from("vendors").select("*").eq("school_id", data.schoolId).order("vendor_name"),
      supabase.from("supplier_invoices").select("*, vendors(vendor_name)").order("invoice_date", { ascending: false }).limit(50),
      supabase.from("procurement_budgets").select("*").eq("school_id", data.schoolId).order("fiscal_year", { ascending: false }),
      supabase.from("procurement_budget_lines").select("*"),
      supabase.from("procurement_audit_logs").select("*").order("created_at", { ascending: false }).limit(50),
    ]);
    return {
      requests: requests.data ?? [], pos: pos.data ?? [], vendors: vendors.data ?? [],
      invoices: invoices.data ?? [], budgets: budgets.data ?? [], budgetLines: budgetLines.data ?? [],
      audit: audit.data ?? [],
    };
  });

export const createProcurementRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string; department: string; budgetCode?: string; neededBy?: string; notes?: string; lines: { item_name: string; quantity: number; unit_of_measure?: string; estimated_unit_price: number }[] }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any; const { userId } = context;
    const total = data.lines.reduce((s, l) => s + l.quantity * l.estimated_unit_price, 0);
    const { data: req, error } = await supabase.from("procurement_requests").insert({
      school_id: data.schoolId, requested_by: userId, department: data.department,
      budget_code: data.budgetCode, needed_by: data.neededBy, notes: data.notes,
      estimated_total_ugx: total, status: "submitted",
    }).select().single();
    if (error) throw error;
    const { error: linesErr } = await supabase.from("procurement_request_lines").insert(
      data.lines.map(l => ({ ...l, request_id: req.id }))
    );
    if (linesErr) throw linesErr;
    await supabase.from("procurement_audit_logs").insert({
      entity_type: "procurement_request", entity_id: req.id, action: "created",
      actor_id: userId, after_state: { total, lines: data.lines.length },
    });
    return { id: req.id };
  });

export const decideProcurementRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { requestId: string; decision: "approve" | "reject"; reason?: string; level?: number }) => d)
  .handler(async ({ data, context }) => {
    const { error, data: r } = await (context.supabase.rpc as any)("post_procurement_transition", {
      _request_id: data.requestId, _decision: data.decision, _reason: data.reason ?? null, _level: data.level ?? 1,
    });
    if (error) throw new Error(error.message);
    return r;
  });

export const issuePurchaseOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string; requestId: string; vendorId: string; expectedDelivery: string; lines: { item_name: string; quantity_ordered: number; unit_price: number }[] }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any; const { userId } = context;
    const subtotal = data.lines.reduce((s, l) => s + l.quantity_ordered * l.unit_price, 0);
    const tax = Math.round(subtotal * 0.18);
    const { data: po, error } = await supabase.from("purchase_orders").insert({
      school_id: data.schoolId, vendor_id: data.vendorId, request_id: data.requestId,
      expected_delivery_date: data.expectedDelivery, subtotal_amount: subtotal, tax_amount: tax,
      total_amount: subtotal + tax, created_by: userId, status: "issued",
    }).select().single();
    if (error) throw error;
    await supabase.from("purchase_order_lines").insert(data.lines.map(l => ({ ...l, purchase_order_id: po.id })));
    await supabase.from("procurement_requests").update({ status: "po_issued" }).eq("id", data.requestId);
    await supabase.from("procurement_audit_logs").insert({
      entity_type: "purchase_order", entity_id: po.id, action: "issued",
      actor_id: userId, after_state: { total: subtotal + tax, vendor: data.vendorId },
    });
    return { id: po.id, po_no: po.po_no };
  });

export const recordGoodsReceipt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { purchaseOrderId: string; remarks?: string; lines: { purchase_order_line_id: string; quantity_received: number; quantity_accepted: number; quantity_rejected: number; condition_notes?: string }[] }) => d)
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any; const { userId } = context;
    const { data: grn, error } = await supabase.from("goods_receipts").insert({
      purchase_order_id: data.purchaseOrderId, received_by: userId, remarks: data.remarks,
    }).select().single();
    if (error) throw error;
    await supabase.from("goods_receipt_lines").insert(data.lines.map(l => ({ ...l, goods_receipt_id: grn.id })));
    for (const l of data.lines) {
      await supabase.rpc("noop" as any, {}); // placeholder to keep async
      await supabase.from("purchase_order_lines").update({ quantity_received: l.quantity_accepted }).eq("id", l.purchase_order_line_id);
    }
    await supabase.from("purchase_orders").update({ status: "received" }).eq("id", data.purchaseOrderId);
    await supabase.from("procurement_audit_logs").insert({
      entity_type: "goods_receipt", entity_id: grn.id, action: "recorded", actor_id: userId,
    });
    return { id: grn.id, grn_no: grn.grn_no };
  });

export const recordSupplierInvoice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { vendorId: string; purchaseOrderId?: string; invoiceNo: string; amount: number; taxAmount?: number; dueDate?: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: inv, error } = await (context.supabase as any).from("supplier_invoices").insert({
      vendor_id: data.vendorId, purchase_order_id: data.purchaseOrderId,
      invoice_no: data.invoiceNo, amount: data.amount, tax_amount: data.taxAmount ?? 0,
      due_date: data.dueDate,
    }).select().single();
    if (error) throw error;
    return { id: inv.id };
  });

export const runThreeWayMatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { invoiceId: string }) => d)
  .handler(async ({ data, context }) => {
    const { error, data: r } = await (context.supabase.rpc as any)("three_way_match", { _invoice_id: data.invoiceId });
    if (error) throw new Error(error.message);
    return r;
  });

export const createVendor = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string; vendor_name: string; category?: string; phone?: string; email?: string; tax_id?: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: v, error } = await (context.supabase as any).from("vendors").insert({
      school_id: data.schoolId, vendor_name: data.vendor_name, category: data.category,
      phone: data.phone, email: data.email, tax_id: data.tax_id,
    }).select().single();
    if (error) throw error;
    return { id: v.id };
  });

export const seedProcurementDemo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { schoolId: string }) => d)
  .handler(async ({ data, context }) => {
    const { error, data: r } = await (context.supabase.rpc as any)("seed_procurement_demo", { _school_id: data.schoolId });
    if (error) throw new Error(error.message);
    return r;
  });
